import { useSyncExternalStore } from 'react'
import { useQuery } from '@tanstack/react-query'
import { hexToHslChannels, type JudgmentMode } from '@biznomi/ui-react'
import { getBranding, type Branding } from './api/auth'
import { getSubdomain } from './org'

/**
 * ⚠️ 이 파일은 judgmentMode 를 다루도록 허용된 두 곳 중 하나다
 *    (다른 하나 = packages/ui-react/src/components/AttendanceTime.tsx).
 *    여기서 하는 일은 "값을 실어 나르는 것"뿐이고, 판정 분기는 하지 않는다.
 *    가드레일 = scripts/guardrails/check-judgment-mode.sh
 */

export type OrgConfig = Branding & { judgmentMode: JudgmentMode }

// ── 개발용 오버라이드 ────────────────────────────────────────
// 서버 값(M2 로 judgment_mode 컬럼 추가됨)이 정답이다. 이 오버라이드는 로컬에서
// 조직 설정을 바꾸지 않고 두 모드를 번갈아 보기 위한 것뿐이다. 프로덕션 빌드에선 무시된다.
const OVERRIDE_KEY = 'biznomi_dev_judgment_mode'
const listeners = new Set<() => void>()

function readOverride(): JudgmentMode | null {
  if (!import.meta.env.DEV || typeof localStorage === 'undefined') return null
  const v = localStorage.getItem(OVERRIDE_KEY)
  return v === 'STANDARD' || v === 'FACTUAL' ? v : null
}

export function setDevModeOverride(mode: JudgmentMode) {
  localStorage.setItem(OVERRIDE_KEY, mode)
  listeners.forEach((l) => l())
}

function subscribe(l: () => void) {
  listeners.add(l)
  return () => listeners.delete(l)
}

/** 개발용 토글이 구독하는 훅. 프로덕션에선 항상 null 이다. */
export function useDevModeOverride(): JudgmentMode | null {
  return useSyncExternalStore(subscribe, readOverride, () => null)
}

// ── 조직 정보 ────────────────────────────────────────────────

/** 조직 정보 — 로그인 전후 모두 필요하다 (로그인 폼 분기, 브랜드 컬러) */
export function useOrg() {
  const subdomain = getSubdomain()
  const override = useDevModeOverride()

  const query = useQuery<Branding | null>({
    queryKey: ['org', subdomain],
    enabled: subdomain !== '',
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await getBranding()
      if (!res.success || !res.data) throw new Error(res.error ?? '조직 정보를 불러오지 못했습니다')
      return res.data
    },
  })

  /*
   * 서버가 준 값을 쓴다 (M2 이후). 기본값은 FACTUAL(사실만) — 판정은 조직이
   * 명시적으로 켜야 켜진다 (D23). 개발 오버라이드는 로컬에서만 앞선다.
   */
  const data: OrgConfig | null = query.data
    ? {
        ...query.data,
        judgmentMode: override ?? (query.data as { judgmentMode?: JudgmentMode }).judgmentMode ?? 'FACTUAL',
      }
    : null

  return { ...query, data }
}

/**
 * 조직 브랜드 컬러를 CSS 변수에 반영.
 * hex(--color-primary)와 HSL 채널(--primary-hsl) 을 함께 세팅한다 —
 * 전자는 tokens.css 를 쓰는 곳, 후자는 shadcn 의 투명도(bg-primary/10)를 위한 것.
 */
export function applyBrandColor(color: string | null | undefined) {
  if (!color) return
  const root = document.documentElement
  root.style.setProperty('--color-primary', color)

  const hsl = hexToHslChannels(color)
  if (!hsl) return
  root.style.setProperty('--primary-hsl', hsl)
  // hover 는 같은 색을 약간 어둡게
  const [h, s, l] = hsl.split(' ')
  const darker = Math.max(0, parseInt(l, 10) - 4)
  root.style.setProperty('--primary-hover-hsl', `${h} ${s} ${darker}%`)
}
